/* eslint-disable */
import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  Container, Card, TextField, Button, Box, MenuItem, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, InputAdornment
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/styles';
import { AESEncryptionParam } from '../../../../helpers/AesEncrypt';
import CommonPageHeader from '../../../CommonPageModules/CommonPageHeader';
import Page from '../../../Page';
import SearchNotFound from '../../../SearchNotFound';
import services from '../Services';
import permissionAuth from 'src/utils/permissionAuth';
import TokenDecoder from 'src/helpers/TokenDecoder';
import { trackPromise } from 'react-promise-tracker';
import { LoadingComponent } from 'src/pages/pageLoader';
import { toast } from 'react-toastify';
import xlsx from 'json-as-xlsx';

const useStyles = makeStyles((theme) => ({
  root: { minHeight: '100%', paddingBottom: '24px', paddingTop: '24px' },
  cardPadding: { padding: '24px' }
}));

export default function CustomerMasterListing() {
  const classes = useStyles();
  const screenCode = 'CUSTOMERMASTER';
  const navigate = useNavigate();
  const [groupList, setGroupList] = useState([]);
  const [modeList, setModeList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [permissionList, setPermissions] = useState({ isGroupFilterEnabled: false });

  const formik = useFormik({
    initialValues: {
      groupID: 0,
      modeOfOperationID: 0,
      customerTypeID: 0,
      isActive: 2
    },
    validationSchema: Yup.object().shape({
      groupID: Yup.number().min(1, 'Group Required').required('Required'),
      modeOfOperationID: Yup.number().min(1, 'Mode of operation is required').required('Required')
    }),
    onSubmit: (values) => {
      trackPromise(GetCustomerListing(values.groupID, values.modeOfOperationID, values.customerTypeID, values.isActive));
    }
  });

  const { errors, touched, handleSubmit, getFieldProps, values, setValues, setFieldValue } = formik;

  useEffect(() => {
    const storedSearch = JSON.parse(sessionStorage.getItem('customerSearchData'));
    getPermissions(storedSearch);
    trackPromise(GetModes());
    trackPromise(GetTypes());
  }, []);

  async function getPermissions(storedSearch) {
    var permissions = await permissionAuth.getPermissionsByScreen(screenCode);
    if (permissions.find((p) => p.permissionCode == 'VIEWCUSTOMERMASTER') === undefined) {
      navigate('/404');
    }
    const isGroupFilterEnabled = permissions.some((p) => p.permissionCode == 'GROUPDROPDOWN');
    setPermissions({ isGroupFilterEnabled });

    if (storedSearch) {
      setValues(storedSearch);
      trackPromise(GetCustomerListing(storedSearch.groupID, storedSearch.modeOfOperationID, storedSearch.customerTypeID, storedSearch.isActive));
      sessionStorage.removeItem('customerSearchData');
    } else {
      setValues({
        ...values,
        groupID: parseInt(TokenDecoder.getGroupFactoryToken().group, 10),
      });
    }
    trackPromise(GetAllGroups());
  }

  async function GetAllGroups() {
    const res = await services.GetAllGroupsForDropDown();
    if (res) setGroupList(res);
  }

  async function GetModes() {
    const res = await services.getModeOfOperation();
    if (res) setModeList(res);
  }

  async function GetTypes() {
    const res = await services.getAvailableCustomerTypes();
    if (res) setTypeList(res);
  }

  async function GetCustomerListing(groupID, modeID, typeID, status) {
    const res = await services.GetCustomerListingData(groupID, modeID, typeID, status);
    if (res.data && res.data.length > 0) {
      setTableData(res.data);
    } else {
      setTableData([]);
      toast.error(res.message);
    }
    setSearchText('');
  }

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFieldValue(name, value);
    setTableData([]);
    setSearchText('');
  };

  const handleEdit = (id, customerName, mappingID, rowIsActive) => {
    sessionStorage.setItem('customerSearchData', JSON.stringify(values));
    sessionStorage.setItem('customerEditData', JSON.stringify({ customerName, mappingID, rowIsActive }));
    let encrypted = AESEncryptionParam(id.toString());
    navigate('/dashboard/customermaster/AddEdit/' + encrypted);
  };

  const clearAll = () => {
    setValues({
      ...values,
      modeOfOperationID: 0,
      customerTypeID: 0,
      isActive: 2
    });
    setTableData([]);
    setSearchText('');
  }

  const selectedModeCode = modeList.find(x => x.modeOfOperationID === parseInt(values.modeOfOperationID, 10))?.modeOfOperationCode;
  const normalizedSearchText = searchText.trim().toLowerCase();

  const highlightSearchMatch = (value) => {
    const textValue = (value ?? '').toString();
    if (!normalizedSearchText) {
      return textValue;
    }

    const escapedSearchText = normalizedSearchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedSearchText, 'ig');
    const result = [];
    let lastIndex = 0;

    for (const match of textValue.matchAll(regex)) {
      const start = match.index;
      const end = start + match[0].length;

      if (lastIndex < start) {
        result.push(
          <React.Fragment key={`text-${lastIndex}`}>
            {textValue.slice(lastIndex, start)}
          </React.Fragment>
        );
      }

      result.push(
        <mark
          key={`match-${start}`}
          style={{
            backgroundColor: '#ffd796',
            color: 'inherit',
            padding: '0 ',
            borderRadius: '2px'
          }}
        >
          {textValue.slice(start, end)}
        </mark>
      );

      lastIndex = end;
    }

    if (lastIndex < textValue.length) {
      result.push(
        <React.Fragment key={`text-${lastIndex}`}>
          {textValue.slice(lastIndex)}
        </React.Fragment>
      );
    }

    return <span>{result}</span>;
  };

  const filteredTableData = normalizedSearchText
    ? tableData.filter((row) => {
      const searchableValues = [
        row.customerCode,
        row.customerName,
        row.nic,
        row.businessRegNo,
        row.brn,
        row.businessRegistrationNo,
        row.nicBrn
      ];

      return searchableValues.some((value) =>
        (value ?? '').toString().toLowerCase().includes(normalizedSearchText)
      );
    })
    : tableData;

  async function createDataForExcel(array) {
    const res = [];
    if (array != null) {
      array.forEach((x) => {
        const vr = {
          'Customer Code': x.customerCode,
          'BRN/NIC': x.businessRegNo ?? x.brn ?? x.businessRegistrationNo ?? x.nic ?? x.nicBrn ?? '',
          'Name': x.customerName,
          'Company/NIC': selectedModeCode === '01' ? x.companyName : selectedModeCode === '02' ? x.nic : '',
          'Customer Type': x.typeName,
          'Status': (x.mappingIsActive && x.masterIsActive) ? 'Active' : 'Inactive'
        };
        res.push(vr);
      });
    }
    return res;
  }

  async function createFile() {
    if (!filteredTableData.length) {
      toast.error('No records to export');
      return;
    }

    const file = await createDataForExcel(filteredTableData);
    const settings = {
      sheetName: 'Customer Master',
      fileName: `Customer_Master_${new Date().toISOString().split('T')[0]}`,
      writeOptions: {}
    };
    const tempcsvHeaders = [
      { label: 'Customer Code', value: 'Customer Code' },
      { label: 'BRN/NIC', value: 'BRN/NIC' },
      { label: 'Name', value: 'Name' },
      { label: 'Company/NIC', value: 'Company/NIC' },
      { label: 'Customer Type', value: 'Customer Type' },
      { label: 'Status', value: 'Status' }
    ];
    const dataA = [
      {
        sheet: 'Customer Master',
        columns: tempcsvHeaders,
        content: file
      }
    ];
    xlsx(dataA, settings);
  }

  return (
    <Page title="Customer Master | FAPS">
      <Container>
        <LoadingComponent />
        <CommonPageHeader
          path={'/dashboard/customermaster/AddEdit/' + AESEncryptionParam('0')}
          btname="Add"
          pageName="Customer Master"
        />
        <Card className={classes.cardPadding} sx={{ mt: 2 }}>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth size="small" label="Group *" name="groupID" value={values.groupID}
                    onChange={handleDropdownChange} error={Boolean(touched.groupID && errors.groupID)}
                    helperText={touched.groupID && errors.groupID} InputProps={{ readOnly: !permissionList.isGroupFilterEnabled }}>
                    <MenuItem value={0}>Select Group</MenuItem>
                    {groupList.map(x => <MenuItem key={x.groupID} value={x.groupID}>{x.groupName}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth size="small" label="Mode of Operation *" name="modeOfOperationID"
                    value={values.modeOfOperationID} onChange={handleDropdownChange}
                    error={Boolean(touched.modeOfOperationID && errors.modeOfOperationID)} helperText={touched.modeOfOperationID && errors.modeOfOperationID}>
                    <MenuItem value={0}>Select Mode</MenuItem>
                    {modeList.map(x => <MenuItem key={x.modeOfOperationID} value={x.modeOfOperationID}>{x.modeOfOperationName}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth size="small" label="Customer Type" name="customerTypeID"
                    value={values.customerTypeID} onChange={handleDropdownChange}>
                    <MenuItem value={0}>All Types</MenuItem>
                    {typeList.map(x => <MenuItem key={x.customerTypeID} value={x.customerTypeID}>{x.customerTypeName}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth size="small" label="Status" name="isActive"
                    value={values.isActive} onChange={handleDropdownChange}>
                    <MenuItem value={2}>All Status</MenuItem>
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={clearAll} color="error" variant="outlined" sx={{ mr: 1 }}>Clear</Button>
                <Button type="submit" color="secondary" variant="contained">Search</Button>
              </Box>
            </Form>
          </FormikProvider>
        </Card>

        <Box sx={{ mt: 3 }}>
          {tableData.length === 0 ? (
            <SearchNotFound searchQuery="Customers" />
          ) : (
            <>
              <TableContainer component={Paper}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <TextField
                    size="small"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchText('')}
                            disabled={!searchText}
                            aria-label="clear search"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      minWidth: { xs: '100%', sm: 420 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Customer Code</TableCell>
                      <TableCell align="center">BRN/NIC</TableCell>
                      <TableCell align="center">Customer Name</TableCell>

                      {selectedModeCode === "01" && <TableCell align="center">Company</TableCell>}

                      <TableCell align="center">Customer Type</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTableData.length > 0 ? (
                      filteredTableData.map((row) => (
                        <TableRow key={row.customerID}>
                          <TableCell align="center">{highlightSearchMatch(row.customerCode)}</TableCell>
                          <TableCell align="center">{highlightSearchMatch(row.businessRegNo ?? row.brn ?? row.businessRegistrationNo ?? row.nic ?? row.nicBrn ?? "-")}</TableCell>
                          <TableCell align="center">{highlightSearchMatch(row.customerName)}</TableCell>

                          {selectedModeCode === "01" && <TableCell align="center">{highlightSearchMatch(row.companyName)}</TableCell>}

                          <TableCell align="center">{row.typeName}</TableCell>
                          <TableCell align="center">
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.4,
                                borderRadius: '999px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                backgroundColor: (row.mappingIsActive && row.masterIsActive) ? '#E7F6EC' : '#FDECEA',
                                color: (row.mappingIsActive && row.masterIsActive) ? '#1B8E3E' : '#C62828',
                                minWidth: 64
                              }}
                            >
                              {(row.mappingIsActive && row.masterIsActive) ? 'Active' : 'Inactive'}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton onClick={() => handleEdit(row.customerID, row.customerName, row.mappingID, row.mappingIsActive && row.masterIsActive)}>
                              <EditIcon sx={{ color: '#000000' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={selectedModeCode === "01" ? 7 : 6}>
                          <SearchNotFound searchQuery={searchText} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={createFile}
                  disabled={filteredTableData.length === 0}
                >
                  EXCEL
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Page>
  );
}